/**
 * E2E Test Harness for CustomComparisonTab
 * 
 * SECURITY MODEL:
 * - This route is ONLY registered when REACT_APP_E2E_MODE=true
 * - The route registration is conditional in UnProtectedRoutes.tsx
 * - Production builds MUST NOT set REACT_APP_E2E_MODE=true
 * - This prevents the fixture route from being accessible in production
 * 
 * PURPOSE:
 * - Provides a deterministic way to test CustomComparisonTab with 105+ rules
 * - No authentication required (bypassed for E2E testing)
 * - Uses fixture data only, no backend calls
 * - Enables Playwright to exercise real Material-UI controls
 * 
 * USAGE:
 * 1. Start app with: REACT_APP_E2E_MODE=true npm start
 * 2. Navigate to: http://localhost:3000/e2e/custom-comparison
 * 3. Run Playwright tests against this route
 */

import React, { useState } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { RuleProvider } from '../../contexts/RuleContext';
import { RuleResourceProvider } from '../../contexts/RuleResourceContext';
import CustomComparisionTab from '../../components/Rules/CustomComparisionTab';
import {
  E2E_RESOURCES,
  E2E_SELECTED_RESOURCE_COLUMNS,
  generateE2ERules,
  E2E_TOLERANCE_DATA,
} from '../../e2e-fixtures/customComparisonFixtures';
import { themeSettings } from '../../styles/Theme';

/**
 * E2E Test Harness Component
 * Wraps CustomComparisonTab with all required contexts and fixture data
 */
const CustomComparisonE2E: React.FC = () => {
  const [ruleCount, setRuleCount] = useState(105);
  const [initialRules] = useState(() => generateE2ERules(ruleCount));
  
  const theme = createTheme(themeSettings('light'));

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h4" gutterBottom color="error">
            ⚠️ E2E TEST MODE - NOT FOR PRODUCTION
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page is only available when <code>REACT_APP_E2E_MODE=true</code>.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current configuration: {ruleCount} rules, {E2E_RESOURCES.length} resources
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Route: <code>/e2e/custom-comparison</code>
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <E2EContextProvider initialRules={initialRules}>
            <CustomComparisionTab selectedResourceIds={E2E_RESOURCES} />
          </E2EContextProvider>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

/**
 * Context provider that supplies fixture data to CustomComparisonTab
 */
const E2EContextProvider: React.FC<{
  children: React.ReactNode;
  initialRules: any[];
}> = ({ children, initialRules }) => {
  return (
    <RuleProvider>
      <RuleResourceProvider>
        <E2EDataInjector initialRules={initialRules}>
          {children}
        </E2EDataInjector>
      </RuleResourceProvider>
    </RuleProvider>
  );
};

/**
 * Component that injects fixture data into contexts after mount
 */
const E2EDataInjector: React.FC<{
  children: React.ReactNode;
  initialRules: any[];
}> = ({ children, initialRules }) => {
  // Import hooks properly
  const { useRuleContext } = require('../../contexts/RuleContext');
  const { useRuleResourceContext } = require('../../contexts/RuleResourceContext');

  const {
    setCustomComparisonRules,
    setToleranceData,
    setSelectedResourceColumns,
  } = useRuleContext();

  const { setAvailColumns } = useRuleResourceContext();

  React.useEffect(() => {
    // Inject fixture data on mount
    setCustomComparisonRules(initialRules);
    setToleranceData(E2E_TOLERANCE_DATA);
    setSelectedResourceColumns(E2E_SELECTED_RESOURCE_COLUMNS);

    // Set available columns from resources
    const allColumns = E2E_RESOURCES.flatMap(r =>
      r.resource_column_properties?.resource_columns
        ?.filter((col: any) => col.is_active)
        .map((col: any) => col.column_name) || []
    );
    setAvailColumns(allColumns);
  }, [
    initialRules,
    setCustomComparisonRules,
    setToleranceData,
    setSelectedResourceColumns,
    setAvailColumns,
  ]);

  return <>{children}</>;
};

export default CustomComparisonE2E;
